/**
 * UpdateNotification Component
 *
 * Displays a non-intrusive banner when a new application version is available.
 * Uses the useVersionCheck hook to detect updates and provides user-friendly
 * options to update immediately or dismiss for current session.
 *
 * Features:
 * - Beautiful gradient banner with smooth animations
 * - Shows version information
 * - Non-blocking (users can continue working)
 * - Session-based dismissal (won't nag repeatedly)
 * - Accessible with proper ARIA labels
 */

import React from 'react';
import { useVersionCheck } from '../../hooks/useVersionCheck';
import './UpdateNotification.css';

export const UpdateNotification = () => {
  const {
    updateAvailable,
    newVersion,
    currentVersion,
    newBuildTime,
    applyUpdate,
    dismissUpdate,
  } = useVersionCheck();

  // Don't render anything if no update is available
  if (!updateAvailable) {
    return null;
  }

  // Format build time for display
  const formatBuildTime = (buildTime) => {
    if (!buildTime) return '';
    try {
      const date = new Date(buildTime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <div
      className="update-notification"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="update-notification__content">
        {/* Icon */}
        <div className="update-notification__icon" aria-hidden="true">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
        </div>

        {/* Text Content */}
        <div className="update-notification__text">
          <strong className="update-notification__title">
            New version available!
          </strong>
          <p className="update-notification__description">
            Version {newVersion} is ready
            {newBuildTime && ` (built ${formatBuildTime(newBuildTime)})`}.
            Update now for the latest features and fixes.
          </p>
          {currentVersion && (
            <p className="update-notification__current-version">
              Current: v{currentVersion}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="update-notification__actions">
          <button
            className="update-notification__button update-notification__button--primary"
            onClick={applyUpdate}
            aria-label="Update application now"
          >
            Update Now
          </button>
          <button
            className="update-notification__button update-notification__button--secondary"
            onClick={dismissUpdate}
            aria-label="Dismiss update notification"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};
